import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  DollarOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined
} from '@ant-design/icons';
import {
  Alert,
  App,
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { rejectClosingRequest } from '../../services/leadService';
import ClosingApprovalModal from './components/ClosingApprovalModal';

const { Title, Text, Paragraph } = Typography;

const ClosingApprovalDetail = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const [lead, setLead] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectForm] = Form.useForm();
  const [submittingReject, setSubmittingReject] = useState(false);

  // State for Reusable Approval Modal
  const [approveModalVisible, setApproveModalVisible] = useState(false);

  // States for Follow Up History
  const [followUps, setFollowUps] = useState<any[]>([]);
  const [followUpLoading, setFollowUpLoading] = useState(false);

  const fetchLeadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`leads/${leadId}`);
      if (response.data?.status === 'success') {
        setLead(response.data.data);
      } else {
        message.error('Gagal memuat rincian data pengajuan closing.');
        navigate('/closings/approvals');
      }
    } catch (error: any) {
      console.error('Fetch lead detail for closing failed:', error);
      message.error(error.response?.data?.message || 'Gagal memuat rincian data.');
      navigate('/closings/approvals');
    } finally {
      setLoading(false);
    }
  }, [leadId, navigate, message]);

  const fetchFollowUps = useCallback(async () => {
    setFollowUpLoading(true);
    try {
      const response = await api.get(`leads/${leadId}/follow-ups`);
      if (response.data?.status === 'success') {
        setFollowUps(response.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch follow-ups for approval detail:', error);
    } finally {
      setFollowUpLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchLeadDetail();
    fetchFollowUps();
  }, [fetchLeadDetail, fetchFollowUps]);

  const handleRejectSubmit = async () => {
    const activeReq = lead?.active_closing_request;
    if (!activeReq) return;

    try {
      const values = await rejectForm.validateFields();
      setSubmittingReject(true);
      const response = await rejectClosingRequest(activeReq.id, {
        rejection_reason: values.rejection_reason,
      });

      if (response.data?.status === 'success') {
        message.success('Pengajuan closing berhasil ditolak.');
        setRejectModalVisible(false);
        rejectForm.resetFields();
        fetchLeadDetail();
      } else {
        message.error(response.data?.message || 'Gagal menolak pengajuan.');
      }
    } catch (error: any) {
      console.error('Reject closing failed:', error);
      message.error(error.response?.data?.message || 'Gagal menolak pengajuan.');
    } finally {
      setSubmittingReject(false);
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getFollowUpStatusColor = (status: string) => {
    if (!status) return 'default';
    const statusName = status.toLowerCase();
    if (statusName === 'hot') return 'error';
    if (statusName === 'rsv') return 'green';
    if (statusName === 'cold') return 'processing';
    return 'default';
  };

  const followUpColumns = [
    {
      title: 'Tanggal',
      dataIndex: 'created_at',
      key: 'date',
      render: (text: string) => dayjs(text).format('DD MMM YYYY, HH:mm'),
    },
    {
      title: 'Action',
      dataIndex: 'Action',
      key: 'action',
      render: (action: any) => action?.name || '-',
    },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'status',
      render: (status: any) => (
        <Tag color={getFollowUpStatusColor(status?.name)}>
          {status?.name?.toUpperCase() || '-'}
        </Tag>
      ),
    },
    {
      title: 'Respon / Notes',
      dataIndex: 'notes',
      key: 'notes',
    },
    {
      title: 'Oleh',
      dataIndex: 'User',
      key: 'user',
      render: (usr: any) => usr?.full_name || '-',
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" tip="Memuat Detail Pengajuan Closing..." />
      </div>
    );
  }

  if (!lead) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Title level={4}>Data lead tidak ditemukan.</Title>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/leads/approvals')}>
          Kembali ke Antrean
        </Button>
      </div>
    );
  }

  const activeReq = lead.active_closing_request;

  return (
    <div style={{ padding: '24px' }}>
      {/* Back Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/leads/approvals')}
            style={{ marginRight: 16 }}
          >
            Kembali ke Antrean
          </Button>
          <Title level={2} style={{ margin: 0, display: 'inline', verticalAlign: 'middle' }}>
            Detail Pengajuan Closing OK
          </Title>
        </Col>
        <Col>
          {activeReq && activeReq.status === 'pending' && (
            <Space>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                style={{ backgroundColor: '#7BAA18', borderColor: '#7BAA18' }}
                onClick={() => setApproveModalVisible(true)}
              >
                Approve Closing
              </Button>

              <Button
                type="primary"
                danger
                icon={<CloseOutlined />}
                onClick={() => setRejectModalVisible(true)}
              >
                Tolak Pengajuan
              </Button>
            </Space>
          )}

          {activeReq && activeReq.status === 'approved' && (
            <Badge
              status="success"
              text={<span style={{ fontWeight: 'bold', color: '#7BAA18', fontSize: '16px' }}>APPROVED 🎉</span>}
              style={{ padding: '6px 16px', border: '1px solid #7BAA18', backgroundColor: '#f6ffed', borderRadius: '4px' }}
            />
          )}

          {activeReq && activeReq.status === 'rejected' && (
            <Badge
              status="error"
              text={<span style={{ fontWeight: 'bold', color: '#ff4d4f', fontSize: '16px' }}>REJECTED ❌</span>}
              style={{ padding: '6px 16px', border: '1px solid #ff4d4f', backgroundColor: '#fff2f0', borderRadius: '4px' }}
            />
          )}
        </Col>
      </Row>

      {/* Banner status */}
      {activeReq && activeReq.status === 'pending' && (
        <Alert
          message="Pengajuan Closing OK Menunggu Persetujuan Anda ⌛"
          description={
            <div>
              <p>Diajukan oleh sales agent <strong>{activeReq.submitted_by_name || lead.assigned_to?.full_name || 'N/A'}</strong> pada {dayjs(activeReq.created_at).format('DD MMM YYYY, HH:mm')}.</p>
              <p>Silakan tinjau seluruh kelengkapan data pembeli, nominal omzet, serta tautan dokumen fisik pendukung di bawah sebelum memberikan keputusan.</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24, borderRadius: 6 }}
        />
      )}

      {activeReq && activeReq.status === 'rejected' && (
        <Alert
          message="Pengajuan Closing OK Telah Ditolak ❌"
          description={
            <div>
              <p>Pengajuan closing ini telah ditolak oleh Atasan pada {activeReq.processed_at ? dayjs(activeReq.processed_at).format('DD MMM YYYY, HH:mm') : '-'}.</p>
              <p style={{ color: '#cf1322', fontWeight: 'bold', marginTop: 4 }}>Alasan Penolakan: {activeReq.rejection_reason}</p>
            </div>
          }
          type="error"
          showIcon
          style={{ marginBottom: 24, borderRadius: 6 }}
        />
      )}

      {activeReq && activeReq.status === 'approved' && (
        <Alert
          message="Pengajuan Closing OK Telah Disetujui 🎉"
          description={
            <div>
              <p>Pengajuan closing telah disetujui pada {activeReq.processed_at ? dayjs(activeReq.processed_at).format('DD MMM YYYY, HH:mm') : '-'}. Status lead ini kini aktif sebagai <strong>O K (Closed)</strong>.</p>
            </div>
          }
          type="success"
          showIcon
          style={{ marginBottom: 24, borderRadius: 6 }}
        />
      )}

      {!activeReq && (
        <Alert
          message="Tidak Ada Pengajuan Closing Aktif"
          description="Lead ini saat ini tidak memiliki riwayat pengajuan closing."
          type="warning"
          showIcon
          style={{ marginBottom: 24, borderRadius: 6 }}
        />
      )}

      <Row gutter={[16, 16]}>
        {/* Left Side: Buyer Info & Transaction Details */}
        <Col xs={24} lg={16}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {activeReq && (
              <Card title={
                <Space>
                  <UserOutlined />
                  <span>Data Pembeli (Buyer Info)</span>
                </Space>
              }>
                <Descriptions bordered column={2}>
                  <Descriptions.Item label="Nama Pembeli" span={2}>
                    <Text strong>{activeReq.buyer_name}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="No. Telepon">
                    {activeReq.buyer_phone ? (
                      <a href={`tel:${activeReq.buyer_phone}`}>
                        <PhoneOutlined style={{ marginRight: 4 }} />
                        {activeReq.buyer_phone}
                      </a>
                    ) : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {activeReq.buyer_email ? (
                      <a href={`mailto:${activeReq.buyer_email}`}>
                        <MailOutlined style={{ marginRight: 4 }} />
                        {activeReq.buyer_email}
                      </a>
                    ) : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Hubungan dengan Lead">
                    <Tag color="purple">{activeReq.relationship_with_lead}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="NIK KTP">
                    {activeReq.buyer_nik || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="NPWP">
                    {activeReq.buyer_npwp || '-'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {activeReq && (
              <Card title={
                <Space>
                  <DollarOutlined />
                  <span>Rincian Unit & Transaksi</span>
                </Space>
              }>
                <Descriptions bordered column={2}>
                  <Descriptions.Item label="Unit Properti yang Dibeli" span={2}>
                    <Text strong>{activeReq.purchased_property_detail}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Nominal Omzet Real (Deal)" span={2}>
                    <Text type="success" strong style={{ fontSize: '18px' }}>
                      {formatCurrency(activeReq.omzet)}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Dokumen Pendukung / SPR" span={2}>
                    {activeReq.supporting_documents ? (
                      <Button
                        type="primary"
                        icon={<FolderOpenOutlined />}
                        href={activeReq.supporting_documents}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                      >
                        Buka Link Berkas Pendukung (Folder Drive)
                      </Button>
                    ) : (
                      <Text type="secondary">Tidak ada lampiran dokumen.</Text>
                    )}
                  </Descriptions.Item>
                  {activeReq.sp_document && (
                    <Descriptions.Item label="Dokumen Surat Pesanan (SP)" span={2}>
                      <Button
                        type="primary"
                        icon={<FilePdfOutlined />}
                        href={activeReq.sp_document}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ backgroundColor: '#7BAA18', borderColor: '#7BAA18' }}
                      >
                        Buka Dokumen Surat Pesanan (PDF)
                      </Button>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}

            {activeReq && activeReq.notes && (
              <Card title={
                <Space>
                  <FileTextOutlined />
                  <span>Catatan dari Sales</span>
                </Space>
              }>
                <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                  {activeReq.notes}
                </Paragraph>
              </Card>
            )}

            {/* Riwayat Follow Up Activities Recap */}
            <Card title={
              <Space>
                <FileTextOutlined />
                <span>Riwayat Follow Up Lead</span>
              </Space>
            }>
              <Table
                dataSource={followUps}
                columns={followUpColumns}
                rowKey="id"
                pagination={{ pageSize: 5 }}
                size="small"
                loading={followUpLoading}
                locale={{ emptyText: 'Belum ada riwayat follow up.' }}
              />
            </Card>
          </Space>
        </Col>

        {/* Right Side: Lead Profile Overview */}
        <Col xs={24} lg={8}>
          <Card title="Ikhtisar Profil Lead">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <Avatar size={64} icon={<UserOutlined />} style={{ backgroundColor: '#7BAA18' }} />
                <Title level={4} style={{ marginTop: 12, marginBottom: 4 }}>{lead.full_name}</Title>
                <Tag color="cyan">{lead.property_area?.name || 'N/A'}</Tag>
              </div>

              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Sales Assigned">
                  {lead.assigned_to?.full_name || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Email Lead">
                  {lead.email || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Telepon Lead">
                  {lead.phone || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Prioritas">
                  <Tag color={getFollowUpStatusColor(lead.lead_priority?.name)}>
                    {lead.lead_priority?.name?.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Budget Awal">
                  {formatCurrency(lead.budget)}
                </Descriptions.Item>
                <Descriptions.Item label="Sumber">
                  <Tag color="blue">{lead.lead_source?.name}</Tag>
                </Descriptions.Item>
              </Descriptions>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Reusable Closing Approval Modal with SP Document Upload & Live Mini Preview */}
      <ClosingApprovalModal
        open={approveModalVisible}
        onCancel={() => setApproveModalVisible(false)}
        onSuccess={() => {
          setApproveModalVisible(false);
          fetchLeadDetail();
        }}
        request={activeReq}
        leadName={lead?.full_name}
      />

      {/* Reject Modal */}
      <Modal
        title="Tolak Pengajuan Closing OK"
        open={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false);
          rejectForm.resetFields();
        }}
        onOk={handleRejectSubmit}
        confirmLoading={submittingReject}
        okText="Tolak Pengajuan"
        cancelText="Batal"
        okButtonProps={{ danger: true }}
      >
        <Form form={rejectForm} layout="vertical">
          <p>
            Tolak pengajuan closing untuk pembeli <strong>{activeReq?.buyer_name}</strong> senilai{' '}
            <strong>{formatCurrency(activeReq?.omzet || 0)}</strong>.
          </p>
          <Form.Item
            name="rejection_reason"
            label="Alasan Penolakan"
            rules={[{ required: true, message: 'Alasan penolakan wajib diisi!' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Masukkan alasan lengkap penolakan pengajuan closing ini agar sales agent dapat memperbaiki berkas/informasi..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ClosingApprovalDetail;
